import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileDownload from 'js-file-download';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
} from 'material-react-table';
import ApiAnalyticsService, {
  type ApiUsage,
  type ClientUsage,
} from '../../../Services/ApiAnalyticsService';
import Notification from '../../Layout/Notification';
import ApiEventsDialog, { type EventsTarget } from './ApiEventsDialog';
import ApiUsageHeatmap from './ApiUsageHeatmap';
import { extractRows, formatDuration, formatTimestamp, toIsoOrNull } from './apiAnalyticsUtils';
import { useTheme } from '@mui/material/styles';
import './ApiAnalytics.scss';

const RETENTION_NOTICE =
  'Analytics is derived from the API audit log, which is retained for a limited period. ' +
  '"Not Used" means no calls were recorded within the retention window and the selected ' +
  'date range - not that the endpoint was never used.';

// The per-client endpoint list is aggregated in full on the server and is small (bounded by the
// number of catalogued endpoints), so we fetch it all in one page and let the table sort and
// paginate client-side. This keeps every column sortable without relying on server-side sorting.
const ALL_ROWS_SIZE = 1000;

const ApiAnalytics = () => {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  // filters
  const [clients, setClients] = useState<ClientUsage[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [used, setUsed] = useState(true);
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  // table (fetched in full and sorted / paginated client-side)
  const [apis, setApis] = useState<ApiUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'requestCount', desc: true }]);
  const fetchIdRef = useRef(0);

  // drill-down
  const [eventsTarget, setEventsTarget] = useState<EventsTarget | null>(null);

  // export + audit data window (advertised via response headers)
  const [exporting, setExporting] = useState(false);
  const [dataWindow, setDataWindow] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  });

  const effectiveClientId = selectedClient;
  const fromIso = useMemo(() => toIsoOrNull(from), [from]);
  const toIso = useMemo(() => toIsoOrNull(to), [to]);

  // load the client dropdown once
  useEffect(() => {
    let active = true;
    setClientsLoading(true);
    ApiAnalyticsService.getClients({ page: 0, size: 500 })
      .then(response => {
        if (!active) return;
        const rows = extractRows<ClientUsage>(response);
        setClients(rows);
        // preselect the busiest client so the view is never empty on first open
        const busiest = [...rows].sort((a, b) => (b.requestCount ?? 0) - (a.requestCount ?? 0))[0];
        if (busiest) {
          setSelectedClient(prev => prev || busiest.clientId);
        }
      })
      .catch((err: unknown) => {
        if (active) Notification.fromError(err, { fallbackTitle: "Couldn't load clients" });
      })
      .finally(() => {
        if (active) setClientsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const fetchApis = useCallback(async () => {
    if (!effectiveClientId) {
      setApis([]);
      return;
    }
    const id = ++fetchIdRef.current;
    setLoading(true);
    try {
      const response = await ApiAnalyticsService.getClientApis(effectiveClientId, {
        used,
        from: fromIso,
        to: toIso,
        page: 0,
        size: ALL_ROWS_SIZE,
      });
      if (id !== fetchIdRef.current) return;
      setApis(extractRows<ApiUsage>(response));
      setDataWindow({
        start: (response.headers?.['x-data-window-start'] as string | undefined) ?? null,
        end: (response.headers?.['x-data-window-end'] as string | undefined) ?? null,
      });
    } catch (err: unknown) {
      if (id !== fetchIdRef.current) return;
      Notification.fromError(err, { fallbackTitle: "Couldn't load API usage" });
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [effectiveClientId, used, fromIso, toIso]);

  useEffect(() => {
    fetchApis();
  }, [fetchApis]);

  const handleExport = useCallback(async () => {
    if (!effectiveClientId) return;
    setExporting(true);
    try {
      const response = await ApiAnalyticsService.exportClientAnalytics(effectiveClientId, {
        from: fromIso,
        to: toIso,
      });
      FileDownload(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `api-analytics-${effectiveClientId}.xlsx`
      );
    } catch (err: unknown) {
      Notification.fromError(err, { fallbackTitle: "Couldn't export analytics" });
    } finally {
      setExporting(false);
    }
  }, [effectiveClientId, fromIso, toIso]);

  const columns = useMemo<MRT_ColumnDef<ApiUsage>[]>(
    () => [
      {
        accessorKey: 'httpMethod',
        header: 'Method',
        size: 90,
        Cell: ({ cell }) => (
          <Chip label={cell.getValue<string>()} size="small" variant="outlined" />
        ),
      },
      { accessorKey: 'endpoint', header: 'Endpoint', size: 320 },
      {
        accessorKey: 'requestCount',
        header: 'Requests',
        size: 110,
        Cell: ({ cell }) => cell.getValue<number>()?.toLocaleString() ?? '0',
      },
      {
        accessorKey: 'errorCount',
        header: 'Errors',
        size: 100,
        Cell: ({ cell }) => {
          const errors = cell.getValue<number>() ?? 0;
          return errors > 0 ? (
            <Chip label={errors} size="small" color="warning" variant="outlined" />
          ) : (
            '0'
          );
        },
      },
      {
        accessorKey: 'avgDurationMs',
        header: 'Avg duration',
        size: 130,
        Cell: ({ cell }) => formatDuration(cell.getValue<number | null>()),
      },
      {
        accessorKey: 'lastUsed',
        header: 'Last used',
        size: 190,
        Cell: ({ cell }) => formatTimestamp(cell.getValue<string | null>()),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    data: apis,
    columns,
    manualFiltering: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableTopToolbar: false,
    initialState: { density: 'compact', pagination: { pageIndex: 0, pageSize: 10 } },
    state: {
      sorting,
      isLoading: loading,
    },
    onSortingChange: updater =>
      setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater)),
    paginationDisplayMode: 'pages',
    // only "used" rows have events to drill into
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () =>
        row.original.used &&
        setEventsTarget({
          clientId: effectiveClientId,
          httpMethod: row.original.httpMethod,
          endpoint: row.original.endpoint,
        }),
      sx: {
        cursor: row.original.used ? 'pointer' : 'default',
        '&:hover': row.original.used ? { backgroundColor: 'rgba(94,51,191,0.04)' } : {},
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { border: '1px solid var(--t-border, rgba(0,0,0,0.12))', borderRadius: 2 },
    },
    muiTableHeadCellProps: {
      sx: { fontWeight: 700, fontSize: 13, borderBottom: `2px solid ${ACCENT}` },
    },
    muiTableBodyCellProps: { sx: { fontSize: 13 } },
    renderEmptyRowsFallback: () => (
      <Box className="aa-empty-state">
        {!effectiveClientId
          ? 'Select a client to see its API usage.'
          : used
            ? 'No API calls recorded for this client in the selected range.'
            : 'This client has used every catalogued endpoint in the selected range.'}
      </Box>
    ),
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="aa-container">
        <Alert severity="info" icon={<InfoOutlinedIcon />} className="aa-retention">
          {RETENTION_NOTICE}
          {dataWindow.start && dataWindow.end && (
            <>
              {' '}
              Audit data currently available from {formatTimestamp(dataWindow.start)} to{' '}
              {formatTimestamp(dataWindow.end)}.
            </>
          )}
        </Alert>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} className="aa-filters">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="aa-client-label">Client</InputLabel>
            <Select
              labelId="aa-client-label"
              label="Client"
              value={selectedClient}
              onChange={e => setSelectedClient(e.target.value)}
            >
              {clientsLoading && (
                <MenuItem value="" disabled>
                  Loading…
                </MenuItem>
              )}
              {!clientsLoading && clients.length === 0 && (
                <MenuItem value="" disabled>
                  No clients found
                </MenuItem>
              )}
              {clients.map(c => (
                <MenuItem key={c.clientId} value={c.clientId}>
                  {c.clientId} ({c.requestCount.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateTimePicker
            label="From"
            value={from}
            onChange={setFrom}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
          <DateTimePicker
            label="To"
            value={to}
            onChange={setTo}
            minDateTime={from ?? undefined}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={used ? 'used' : 'notUsed'}
            onChange={(_e, v: string | null) => {
              if (!v) return;
              const nextUsed = v === 'used';
              setUsed(nextUsed);
              // sensible default sort per view: busiest first vs. alphabetical catalog
              setSorting(
                nextUsed ? [{ id: 'requestCount', desc: true }] : [{ id: 'endpoint', desc: false }]
              );
            }}
            className="aa-used-toggle"
          >
            <ToggleButton value="used">Used APIs</ToggleButton>
            <ToggleButton value="notUsed">Not Used APIs</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            size="small"
            className="aa-export-button"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={!effectiveClientId || exporting}
            onClick={handleExport}
          >
            {exporting ? 'Exporting…' : 'Export .xlsx'}
          </Button>
        </Stack>

        <MaterialReactTable table={table} />

        <ApiUsageHeatmap clientId={effectiveClientId} fromIso={fromIso} toIso={toIso} />
      </Box>

      <ApiEventsDialog
        target={eventsTarget}
        from={fromIso}
        to={toIso}
        onClose={() => setEventsTarget(null)}
      />
    </LocalizationProvider>
  );
};

export default ApiAnalytics;
